import { ref } from 'vue';
import { getTaskflowAgentHistoryApi } from '@/api/taskFlowAgent';

const taskFlowConversationList = ref([]);
const taskFlowConversationListRaw = ref([]);
const taskFlowConversationListLoading = ref(false);

function normalizeTaskFlowHistoryList(res) {
  if (Array.isArray(res)) return res;
  if (res && Array.isArray(res.list)) return res.list;
  if (res && Array.isArray(res.data)) return res.data;
  return [];
}

/**
 * @param {{ robotId: string|number|undefined, messageSource: string, homelandId: string|number|undefined }} p
 */
async function fetchTaskFlowConversationList(p) {
  const { robotId, messageSource, homelandId } = p || {};
  if (!robotId) {
    taskFlowConversationList.value = [];
    taskFlowConversationListRaw.value = [];
    return;
  }
  taskFlowConversationListLoading.value = true;
  try {
    const res = await getTaskflowAgentHistoryApi({
      robot_id: Number(robotId),
      use_type: 'bot',
      message_source: messageSource,
      homeland_id: homelandId
    });
    const raw = normalizeTaskFlowHistoryList(res);
    taskFlowConversationListRaw.value = raw;
    taskFlowConversationList.value = raw.filter(i => i && i.title);
  } catch (e) {
    console.error('fetchTaskFlowConversationList', e);
    taskFlowConversationList.value = [];
    taskFlowConversationListRaw.value = [];
  } finally {
    taskFlowConversationListLoading.value = false;
  }
}

const taskFlowDownloadSessionUuid = ref('');

/**
 * @param {{ robotId: string|number|undefined, messageSource: string, homelandId: string|number|undefined }} p
 */
async function refreshTaskFlowDownloadSessionUuid(p) {
  const { robotId, messageSource, homelandId } = p || {};
  if (!robotId) {
    taskFlowDownloadSessionUuid.value = '';
    return;
  }
  try {
    await fetchTaskFlowConversationList({ robotId, messageSource, homelandId });
    const list = taskFlowConversationListRaw.value || [];
    const hit = list.find(
      i =>
        i &&
        (i.is_pinned === 0 ||
          i.is_pinned === '0' ||
          Number(i.is_pinned) === 0)
    );
    taskFlowDownloadSessionUuid.value = hit?.session_uuid
      ? String(hit.session_uuid)
      : '';
  } catch (e) {
    console.error('refreshTaskFlowDownloadSessionUuid', e);
    taskFlowDownloadSessionUuid.value = '';
  }
}

const taskFlowAgentInfo = ref({});

function setTaskFlowAgentInfo(data) {
  taskFlowAgentInfo.value =
    data && typeof data === 'object' ? { ...data } : {};
}

/**
 */
const taskFlowAgentFlowData = ref(null);

function setTaskFlowAgentFlowData(data) {
  if (data == null) {
    taskFlowAgentFlowData.value = null;
    return;
  }
  if (typeof data !== 'object') {
    taskFlowAgentFlowData.value = null;
    return;
  }
  taskFlowAgentFlowData.value = { ...data };
}

const workflowMetaState = ref({ sessionId: '', fileIdMap: {} });

function setWorkflowMetaState(newState) {
  if (newState && typeof newState === 'object') {
    workflowMetaState.value = { ...newState };
  }
}

function resetWorkflowMetaState() {
  workflowMetaState.value = { sessionId: '', fileIdMap: {} };
}

const aiFlowTestParams = ref({});
const debuggerFlow = ref(false);
const taskFlowConversationListVisible = ref(false);
const debuggerSegmentCode = ref('');
const debuggerSegmentConversationItem = ref(null);
const debuggerChatList = ref([]);

function createEmptyDebuggerInfo() {
  return {
    jsonMode: false,
    jsonInputStr: '{}',
    outputText: '',
    outputType: '',
    wfVarInputValues: {},
    filePickState: {},
    uploadedFileVars: {},
    nodeExecutionEvents: [],
    lastNodeException: null
  };
}

const curDebuggerInfo = ref(createEmptyDebuggerInfo());

/**
 * @param {Array<{ rowKey: string, defaultValue?: any }>} variableRows wf_input
 */
function resetCurDebuggerInfoDefaults(variableRows = []) {
  const wfVarInputValues = {};
  variableRows.forEach(r => {
    if (r?.rowKey) wfVarInputValues[r.rowKey] = r?.defaultValue ?? '';
  });
  curDebuggerInfo.value = {
    ...createEmptyDebuggerInfo(),
    wfVarInputValues
  };
}

export default () => {
  const updateAiFlowTestParams = async params => {
    aiFlowTestParams.value = params;
  };
  const setDebuggerFlow = visible => {
    debuggerFlow.value = visible;
    if (!visible) {
      taskFlowConversationListVisible.value = false;
    }
  };
  const setTaskFlowConversationListVisible = v => {
    taskFlowConversationListVisible.value = !!v;
  };
  const toggleTaskFlowConversationList = () => {
    taskFlowConversationListVisible.value =
      !taskFlowConversationListVisible.value;
  };
  const setDebuggerSegmentCode = code => {
    debuggerSegmentCode.value = code || '';
    if (!debuggerSegmentCode.value) {
      debuggerSegmentConversationItem.value = null;
    }
  };
  const setDebuggerSegmentConversationItem = item => {
    if (item == null || typeof item !== 'object') {
      debuggerSegmentConversationItem.value = null;
      return;
    }
    debuggerSegmentConversationItem.value = { ...item };
  };
  const setDebuggerChatList = list => {
    debuggerChatList.value = Array.isArray(list) ? list : [];
  };

  return {
    taskFlowAgentInfo,
    setTaskFlowAgentInfo,
    taskFlowAgentFlowData,
    setTaskFlowAgentFlowData,
    taskFlowConversationList,
    taskFlowConversationListRaw,
    taskFlowConversationListLoading,
    fetchTaskFlowConversationList,
    taskFlowDownloadSessionUuid,
    refreshTaskFlowDownloadSessionUuid,
    aiFlowTestParams,
    updateAiFlowTestParams,
    debuggerFlow,
    setDebuggerFlow,
    taskFlowConversationListVisible,
    setTaskFlowConversationListVisible,
    toggleTaskFlowConversationList,
    debuggerSegmentCode,
    setDebuggerSegmentCode,
    debuggerSegmentConversationItem,
    setDebuggerSegmentConversationItem,
    debuggerChatList,
    setDebuggerChatList,
    curDebuggerInfo,
    resetCurDebuggerInfoDefaults,
    workflowMetaState,
    setWorkflowMetaState,
    resetWorkflowMetaState
  };
};
